"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";

import type { ServiceDetails } from "@louez/types";
import { hasServiceDetailsContent } from "@louez/types";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@louez/ui";
import { ChevronsUpDownIcon } from "@louez/ui/icons";
import { cn } from "@louez/utils";

type AccordionSection = {
  id: string;
  title: string;
  items?: string[];
  text?: string | null;
};

type ServiceDetailSectionsProps = {
  className?: string;
  details: ServiceDetails;
};

export const ServiceDetailSections = ({
  className,
  details,
}: ServiceDetailSectionsProps) => {
  const t = useTranslations("storefront.services");
  const sections: AccordionSection[] = [
    {
      id: "howItWorks",
      title: t("detailsHowItWorks"),
      items: details.howItWorks,
    },
    {
      id: "includes",
      title: t("detailsIncludes"),
      items: details.includes,
    },
    {
      id: "notes",
      title: t("detailsNotes"),
      items: details.notes,
    },
    {
      id: "afterRequest",
      title: t("detailsAfterRequest"),
      text: details.afterRequest,
    },
  ].filter(
    (section) =>
      (section.items && section.items.length > 0) ||
      Boolean(section.text?.trim()),
  );
  const [openSectionId, setOpenSectionId] = useState<string | null>(
    sections[0]?.id ?? null,
  );

  if (!hasServiceDetailsContent(details) || sections.length === 0) {
    return null;
  }

  return (
    <section className={cn("space-y-4", className)} aria-label={t("serviceDetails")}>
      <h2 className="text-xl font-semibold">{t("serviceDetails")}</h2>
      <div className="divide-border border-border divide-y overflow-hidden rounded-xl border">
        {sections.map((section) => {
          const isOpen = openSectionId === section.id;
          return (
            <Collapsible
              key={section.id}
              open={isOpen}
              onOpenChange={(open) =>
                setOpenSectionId(open ? section.id : null)
              }
            >
              <CollapsibleTrigger className="hover:bg-muted/50 flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left text-sm font-medium">
                <span>{section.title}</span>
                <ChevronsUpDownIcon
                  className={cn(
                    "text-muted-foreground size-4 shrink-0 transition-transform",
                    isOpen && "rotate-180",
                  )}
                />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="text-muted-foreground px-4 pb-4 text-sm leading-6">
                  {section.items && section.items.length > 0 ? (
                    <ol className="list-decimal space-y-2 pl-4">
                      {section.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ol>
                  ) : (
                    <p>{section.text}</p>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </div>
    </section>
  );
};
